import {
  ActionIcon,
  Button,
  Card,
  Center,
  createStyles,
  Drawer,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  SimpleGrid,
  Table,
  Tabs,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import axios from "axios";
import StockAddModal from "components/Stock/StockAddModal";
import StockDetailModal from "components/Stock/StockDetail";
import ActionButton from "components/controls/ActionButtonNew";
import { customLoader } from "components/utilities/loader";
import { Fragment, useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { Plus } from "tabler-icons-react";
import { API, formatNumber, PAGE_SIZE_OPTIONS } from "utiles/url";
import MinmumStock from "./minimumStock";

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  thh: {
    color: "#666666",
    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
    fontSize: "10px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  idColumn: {
    width: "3%",
  },
  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  searchInput: {
    flexGrow: 1,
    paddingRight: "50px", // Add padding to avoid text overlap with button
  },
  searchButton: {
    position: "absolute",
    right: 0,
    borderRadius: "0 4px 4px 0",
    height: "70%",
    width: "40px", // Fixed width for the button
    backgroundColor: "#FF6A00",
    color: "#FFFFFF",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "14px",
    cursor: "pointer",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart">
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const Drivers = () => {
  const { classes } = useStyles();
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [editId, setEditId] = useState();
  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [openedDetail, setOpenedDetail] = useState(false);
  const [size, setSize] = useState("10");
  const [activeTab, setActiveTab] = useState("first");

  const theme = useMantineTheme();

  // Fetch data when page or size changes
  useEffect(() => {
    fetchData(size);
  }, [activePage, size]);

  const fetchData = async (size) => {
    setLoading(true);
    try {
      let token = localStorage.getItem("auth_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await axios.get(`${API}/stocks/list/my-stocks`, config);
      if (response.data) {
        setDrivers(response.data.data);
        if (sortBy) {
          setSortedData(
            sortData(response.data.data, {
              sortBy,
              reversed: reverseSortDirection,
              search: "",
            }),
          );
        } else {
          setSortedData(response.data.data);
        }
        setTotal(response.data?.links);
        setTotalPages(response.data.paginatorInfo.lastPage);
        setTotalRecords(response.data.paginatorInfo.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const sortData = (data, payload) => {
    if (!payload.sortBy) {
      return data; // Return original data if no sortBy is specified
    }

    return [...data].sort((a, b) => {
      // Handle the 'name' field specifically
      const valueA = a[payload.sortBy] || ""; // Fallback to empty string if undefined
      const valueB = b[payload.sortBy] || "";

      // Ensure case-insensitive string comparison
      if (payload.reversed) {
        return valueB.localeCompare(valueA, undefined, { sensitivity: "base" });
      }
      return valueA.localeCompare(valueB, undefined, { sensitivity: "base" });
    });
  };

  const handleSort = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setSortBy(field);
    setReverseSortDirection(reversed);
    setSortedData(sortData(drivers, { sortBy: field, reversed, search: "" }));
  };

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
    fetchData(newSize);
  };

  const handleChange = (page) => {
    setActivePage(page);
  };

  const handleDetailStock = (id) => {
    setEditId(id);
    setOpenedDetail(true);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const rows = sortedData?.map((row) => (
    <Fragment key={row.id}>
      <tr>
        <td>{row?.name || "N/A"}</td>
        <td>{row.category?.name || "N/A"}</td>
        <td>
          {row.product_type === "VARIABLE_PRODUCT"
            ? "Variable Product"
            : "Simple Product"}
        </td>
        <td>{formatNumber(row.quantity) || "0"}</td>
        <td>
          <ActionButton
            label="View"
            color="success"
            onClick={() => handleDetailStock(row.id)}
          >
            <FiEye size={15} />
          </ActionButton>
        </td>
      </tr>
    </Fragment>
  ));

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <Tabs
      color="#FF6A00"
      value={activeTab}
      onTabChange={handleTabChange}
      styles={{
        tabList: {
          borderBottom: `2px solid #FF6A00`,
        },
        tab: {
          "&[data-active]": {
            borderColor: "#FF6A00",
          },
          "&:hover": {
            borderColor: "#FF6A00",
          },
        },
      }}
    >
      <Tabs.List>
        <Tabs.Tab value="first">
          <span style={{ color: "#666666", fontWeight: "bold" }}>All</span>
        </Tabs.Tab>
        <Tabs.Tab value="second">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            Minimum Stock
          </span>
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="first">
        <div style={{ width: "98%", margin: "auto" }}>
          <Drawer
            opened={opened}
            onClose={() => setOpened(false)}
            title="Adding Stock"
            padding="xl"
            size="80%"
            position="bottom"
          >
            <StockAddModal
              total={total}
              setTotal={setTotal}
              size={size}
              activePage={activePage}
              setActivePage={setActivePage}
              setOpened={setOpened}
              fetchData={fetchData}
              totalPages={totalPages}
            />
          </Drawer>
          <Drawer
            opened={openedDetail}
            overlayColor={
              theme.colorScheme === "dark"
                ? theme.colors.dark[9]
                : theme.colors.gray[2]
            }
            overlayOpacity={0.55}
            overlayBlur={3}
            title="Product Stocks"
            padding="xl"
            onClose={() => setOpenedDetail(false)}
            position="bottom"
            size="80%"
          >
            <StockDetailModal
              updateData={fetchData}
              size={size}
              id={editId}
              fetchParentData={fetchData}
              setOpenedDetail={setOpenedDetail}
            />
          </Drawer>
          <Card shadow="sm" p="lg">
            <ScrollArea>
              <SimpleGrid cols={3}>
                <div>
                  <Button
                    onClick={() => setOpened(true)}
                    style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
                    leftIcon={<Plus size={14} />}
                  >
                    Add Stock
                  </Button>
                </div>
              </SimpleGrid>
              <Table
                highlightOnHover
                horizontalSpacing="md"
                verticalSpacing="xs"
                sx={{ tableLayout: "fixed", minWidth: 700 }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#F1F1F1" }}>
                    <Th
                      sortable
                      sorted={sortBy === "name"}
                      reversed={reverseSortDirection}
                      onSort={() => handleSort("name")}
                    >
                      <span className={classes.thh}>Product Name</span>
                    </Th>
                    <Th sortable={false}>
                      <span className={classes.thh}>Category</span>
                    </Th>
                    <Th sortable={false}>
                      <span className={classes.thh}>Product Type</span>
                    </Th>
                    <Th sortable={false}>
                      <span className={classes.thh}>Quantity</span>
                    </Th>
                    <Th sortable={false}>
                      <span className={classes.thh}>Actions</span>
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {rows?.length > 0 ? (
                    rows
                  ) : (
                    <tr>
                      <td colSpan={8}>
                        <Text weight={500} align="center">
                          Nothing found
                        </Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "12px",
                  padding: "6px 4px",
                  borderTop: "1px solid #e9ecef",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {/* Left: Total Records */}
                <Text size="sm" style={{ color: "#FF6A00" }}>
                  {`Total Records: ${formatNumber(totalRecords)}`}
                </Text>

                {/* Right: Rows select + navigation */}
                <Group spacing={6} noWrap>
                  <Text size="sm" color="dimmed">
                    Rows
                  </Text>
                  <Select
                    value={size}
                    onChange={handlePageSizeChange}
                    data={PAGE_SIZE_OPTIONS}
                    style={{ width: 72 }}
                    size="xs"
                  />
                  <ActionIcon
                    size="sm"
                    variant="default"
                    onClick={() => handleChange(1)}
                    disabled={activePage <= 1}
                    title="First page"
                    style={{ fontWeight: 700 }}
                  >
                    «
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="default"
                    onClick={() => handleChange(activePage - 1)}
                    disabled={activePage <= 1}
                    title="Previous page"
                  >
                    ‹
                  </ActionIcon>
                  <Text size="sm" style={{ minWidth: 40, textAlign: "center" }}>
                    {activePage} / {totalPages}
                  </Text>
                  <ActionIcon
                    size="sm"
                    variant="default"
                    onClick={() => handleChange(activePage + 1)}
                    disabled={activePage >= totalPages}
                    title="Next page"
                  >
                    ›
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="default"
                    onClick={() => handleChange(totalPages)}
                    disabled={activePage >= totalPages}
                    title="Last page"
                    style={{ fontWeight: 700 }}
                  >
                    »
                  </ActionIcon>
                </Group>
              </div>
            </ScrollArea>
          </Card>
        </div>
      </Tabs.Panel>
      <Tabs.Panel value="second">
        <MinmumStock />
      </Tabs.Panel>
    </Tabs>
  );
};
export default Drivers;
